import { useEffect, useState } from 'react';
import {
  Check,
  Coffee,
  Copy,
  CreditCard,
  ExternalLink,
  HeartHandshake,
  Smartphone,
} from 'lucide-react';
import { appConfig } from '../lib/config';
import { Sheet } from './Sheet';

type Props = {
  onClose: () => void;
};

// Un café redondo: la unidad de donación por defecto.
const COFFEE_PRICE_EUR = 2;
const AMOUNTS = [
  { value: COFFEE_PRICE_EUR, label: 'Un café' },
  { value: 5, label: '5 €' },
  { value: 10, label: '10 €' },
] as const;

// Enlaces de pago de Stripe por importe: puntual y mensual. Son URLs
// públicas de checkout; el importe elegido arriba decide a cuál se va.
const STRIPE_LINKS: Record<number, { once: string; monthly: string }> = {
  2: {
    once: 'https://coffe.aquiviviamos.com/b/9B69ATc0idBSbyGgR6grS00',
    monthly: 'https://coffe.aquiviviamos.com/b/7sYcN50hA55m9qycAQgrS01',
  },
  5: {
    once: 'https://coffe.aquiviviamos.com/b/eVqfZh7K21TadGO9oEgrS02',
    monthly: 'https://coffe.aquiviviamos.com/b/3cIaEX9Sa8hycCK8kAgrS03',
  },
  10: {
    once: 'https://coffe.aquiviviamos.com/b/28E6oH9Sa55mauC0S8grS04',
    monthly: 'https://coffe.aquiviviamos.com/b/dRmdR9ggy8hyfOW58ogrS05',
  },
};

function formatEuros(value: number) {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
  }).format(value);
}

function formatPhone(phone: string) {
  return phone.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');
}

export function DonateSheet({ onClose }: Props) {
  const [amount, setAmount] = useState<number>(COFFEE_PRICE_EUR);
  const [copied, setCopied] = useState(false);
  const { bizumPhone } = appConfig.donation;
  const links = STRIPE_LINKS[amount] ?? STRIPE_LINKS[COFFEE_PRICE_EUR];

  useEffect(() => {
    if (!copied) return;
    const timeout = window.setTimeout(() => setCopied(false), 2_500);
    return () => window.clearTimeout(timeout);
  }, [copied]);

  const copyPhone = async () => {
    if (!bizumPhone) return;
    try {
      await navigator.clipboard.writeText(bizumPhone);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Sheet labelledBy="donate-title" onClose={onClose} closeLabel="Cerrar donaciones">
      <div className="sheet__body donate-sheet__body">
        <span className="donate-sheet__icon" aria-hidden="true">
          <Coffee size={26} />
        </span>
        <h2 id="donate-title" className="sheet__title">
          Invítame a un café
        </h2>
        <p>
          Este proyecto es personal, independiente y sin ánimo de lucro, pero mantenerlo en marcha
          tiene costes reales: las APIs de Google Maps (mapa, buscador, Street View) y la
          infraestructura de base de datos y servidores.{' '}
          <strong>
            Todo lo recaudado se destina íntegramente a pagar esos costes de mantenimiento.
          </strong>
        </p>
        <div className="donate-amounts" role="radiogroup" aria-label="Importe de la donación">
          {AMOUNTS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={amount === option.value}
              onClick={() => setAmount(option.value)}
            >
              <strong>{formatEuros(option.value)}</strong>
              <small>{option.label}</small>
            </button>
          ))}
        </div>
        {bizumPhone && (
          <div className="donate-method">
            <h3>
              <Smartphone size={17} aria-hidden="true" /> Por Bizum
            </h3>
            <p>
              Abre la app de tu banco, entra en <strong>Bizum → Enviar dinero</strong> y envía{' '}
              <strong>{formatEuros(amount)}</strong> a este número:
            </p>
            <div className="donate-phone">
              <span>{formatPhone(bizumPhone)}</span>
              <button
                className="button button--secondary button--small"
                type="button"
                onClick={copyPhone}
              >
                {copied ? (
                  <Check size={16} aria-hidden="true" />
                ) : (
                  <Copy size={16} aria-hidden="true" />
                )}
                {copied ? 'Copiado' : 'Copiar número'}
              </button>
            </div>
          </div>
        )}
        <div className="donate-method">
          <h3>
            <CreditCard size={17} aria-hidden="true" /> Con tarjeta o Apple Pay
          </h3>
          <p>El pago se procesa de forma segura en una página externa.</p>
          <a className="button button--primary" href={links.once} target="_blank" rel="noreferrer">
            Donar {formatEuros(amount)} con tarjeta <ExternalLink size={16} aria-hidden="true" />
          </a>
        </div>
        <div className="donate-method">
          <h3>
            <HeartHandshake size={17} aria-hidden="true" /> Hazte mecenas
          </h3>
          <p>
            El mismo importe <strong>cada mes</strong>, automático y cancelable cuando quieras: la
            forma más útil de sostener los costes fijos del proyecto.
          </p>
          <a
            className="button button--secondary"
            href={links.monthly}
            target="_blank"
            rel="noreferrer"
          >
            Apoyar con {formatEuros(amount)} al mes <ExternalLink size={16} aria-hidden="true" />
          </a>
        </div>
        <p className="donate-footnote">
          Las donaciones son voluntarias y no dan acceso a funciones adicionales: la aplicación es
          igual para todo el mundo.
        </p>
      </div>
    </Sheet>
  );
}
